import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, LogIn } from 'lucide-react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  width: 100%;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const BackButton = styled.button`
  position: absolute;
  top: 24px;
  left: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: white;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Logo = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  padding: 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const LoginButton = styled.button`
  padding: 16px;
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  border: 1px solid #fcc;
  color: #c33;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
`;

const HelpText = styled.div`
  text-align: center;
  font-size: 14px;
  color: #666;
  margin-top: 24px;
`;

export const CustomerLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useCustomerAuth();
  const [cpf, setCpf] = useState('');
  const [otp, setOtp] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [emailHint, setEmailHint] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/customer/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const formatCPF = (value: string) => {
    // Remove caracteres não numéricos
    const numbers = value.replace(/\D/g, '');
    
    // Aplica máscara XXX.XXX.XXX-XX
    if (numbers.length <= 11) {
      const formatted = numbers.replace(
        /(\d{3})(\d{3})(\d{3})(\d{2})/,
        '$1.$2.$3-$4'
      );
      return formatted;
    }
    return value;
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
    setError('');
  };

  const requestOtp = async (cleanCPF: string) => {
    const response = await fetch('/api/customer/login/request-otp', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ cpf: cleanCPF }),
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Erro ao solicitar OTP');
    }

    setOtpRequested(true);
    setEmailHint(data.data?.email_hint || '');
  };

  const verifyOtp = async (cleanCPF: string, code: string) => {
    const response = await fetch('/api/customer/login/verify-otp', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ cpf: cleanCPF, otp: code }),
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Erro ao validar OTP');
    }

    login(data.data.token, data.data.customer);
    navigate('/customer/dashboard');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Remove formatação do CPF
      const cleanCPF = cpf.replace(/\D/g, '');
      
      if (cleanCPF.length !== 11) {
        setError('CPF deve ter 11 dígitos');
        return;
      }

      if (!otpRequested) {
        await requestOtp(cleanCPF);
      } else {
        if (otp.length !== 6) {
          setError('Informe o codigo OTP com 6 digitos');
          return;
        }

        await verifyOtp(cleanCPF, otp);
      }
    } catch (error: any) {
      setError(error?.message || 'Erro de conexao. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <Container>
      <BackButton onClick={handleBack}>
        <ArrowLeft size={16} />
        Voltar
      </BackButton>
      
      <LoginCard>
        <Header>
          <Logo>
            <User size={40} color="white" />
          </Logo>
          <Title>Área do Cliente</Title>
          <Subtitle>
            {!otpRequested
              ? 'Entre com seu CPF para receber o codigo OTP'
              : `Digite o codigo enviado para ${emailHint || 'seu email cadastrado'}`}
          </Subtitle>
        </Header>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label>CPF</Label>
            <Input
              type="text"
              value={cpf}
              onChange={handleCPFChange}
              placeholder="000.000.000-00"
              maxLength={14}
              required
              disabled={otpRequested}
            />
          </InputGroup>

          {otpRequested && (
            <InputGroup>
              <Label>Código OTP</Label>
              <Input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
              />
            </InputGroup>
          )}

          {error && (
            <ErrorMessage>{error}</ErrorMessage>
          )}

          <LoginButton type="submit" disabled={loading}>
            {loading ? (
              otpRequested ? 'Validando...' : 'Enviando OTP...'
            ) : (
              <>
                <LogIn size={20} />
                {otpRequested ? 'Validar e entrar' : 'Receber OTP'}
              </>
            )}
          </LoginButton>
        </Form>

        <HelpText>
          Não possui cadastro? Procure nossa loja para se cadastrar no programa de fidelidade.
        </HelpText>
      </LoginCard>
    </Container>
  );
};
